import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import { Input, requiredInput } from '../../../core/formValidation';
import { ContentTabs } from '../../../core/components/ContentTabs';
import History from '../../taskDetail/components/History';
import TaskDetailNotes from '../../taskDetail/components/TaskDetailNotes';
import { TaskDetailsTab } from './TaskDetailsTab';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { generateToastr } from '../../../core/toastMessages';
import { apiError } from '../../../core/toastMessages';
import { TaskType, Interval } from '../../tasks/models';
import moment from 'moment';
import { IoClose } from 'react-icons/io5';
import { MdChevronLeft } from 'react-icons/md';
import { IoFlag } from 'react-icons/io5';
import { FaAngleDown } from 'react-icons/fa6';
import { TaskInstructionsTab } from './TaskInstructionsTab';
import { TaskMediaTab } from './TaskMediaTab';
import { TaskTemplateLinkedTab } from './TaskTemplateLinkedTab';
import { toLinkedTaskTemplateInputs } from '../helpers';
import { TaskAttachmentsTab } from './TaskAttachmentsTab';
import {
  TaskTemplate,
  TaskTemplateStatus,
  useCreateTaskTemplateMutation,
  useUpdateTaskTemplateMutation,
  useCreateGroupTaskTemplateMutation,
  useUpdateGroupTaskTemplateMutation,
  useSetOccurrenceStatusByIdMutation,
  useSnoozeOccurrenceMutation,
  useRevertOccurrenceSnoozeMutation,
} from '../../../core/api/rtk/taskTemplatesApi';
import ButtonWithLoader from '../../../core/components/ButtonWithLoader';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import UserGroupPermissions, {
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { normalizeProjectStatus } from '../../tasks/WorkOrders/QuickTasks/helpers';
import { projectStatusOptions } from '../../tasks/models';
import { useSwipe } from '../../../core/hooks/useSwipe';
import { renderStatusPill } from '../../tasks/WorkOrders/WorkOrderTable/constants';
import { selectSiteSettingsSite } from '../../settings/reducer';

const PRIORITY_LABELS = ['Low', 'Medium', 'High', 'Urgent'] as const;
const priorityIndexToLabel = (p: any): string => {
  const idx = Number(p);
  return isNaN(idx) ? p || 'Low' : PRIORITY_LABELS[idx] ?? 'Low';
};
const priorityLabelToIndex = (p: any): number => {
  if (typeof p === 'number' && !isNaN(p)) return p;
  const idx = (PRIORITY_LABELS as readonly string[]).indexOf(String(p));
  return idx >= 0 ? idx : 0;
};

const displayStatusToApi = (display: string): TaskTemplateStatus => {
  switch (display) {
    case 'In Progress':
      return 'InProgress';
    case 'Backlog':
      return 'OnHold';
    case 'Planning':
      return 'Planning';
    case 'Review':
      return 'Review';
    case 'Completed':
      return 'Completed';
    default:
      return 'Pending';
  }
};

const EssentialGuard = ({
  active,
  children,
}: {
  active: boolean;
  children: React.ReactChild | React.ReactChildren;
}) =>
  active ? (
    <UserGroupPermissions itemRef={viewPermissionRefs.admin}>{children}</UserGroupPermissions>
  ) : (
    <>{children}</>
  );

interface TaskDetailsProps {
  template?: TaskTemplate | null;
  isNew?: boolean;
  isEditing?: boolean;
  type?: number | null;
  onClose: () => void;
  onSave?: () => void;
  leadingControls?: React.ReactNode;
  subHeader?: React.ReactNode;
}

export const TaskDetails = ({
  template,
  isNew = false,
  isEditing = false,
  type,
  onClose,
  onSave,
  leadingControls,
  subHeader,
}: TaskDetailsProps) => {
  const intl = useIntl();
  const [editing, setEditing] = useState(isNew || isEditing);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const drawerTransitionTime = 300;
  const closingRef = useRef<any | null>(null);

  const siteSettingsSite = useSelector(selectSiteSettingsSite) as {
    groupId?: number | null;
  } | null;
  const groupId: number | null = siteSettingsSite?.groupId ?? null;

  const [createTask, { isLoading: isCreating }] = useCreateTaskTemplateMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskTemplateMutation();
  const [createGroupTask, { isLoading: isCreatingGroup }] = useCreateGroupTaskTemplateMutation();
  const [updateGroupTask, { isLoading: isUpdatingGroup }] = useUpdateGroupTaskTemplateMutation();
  const [setOccurrenceStatus] = useSetOccurrenceStatusByIdMutation();
  const [snoozeOccurrence] = useSnoozeOccurrenceMutation();
  const [revertOccurrenceSnooze] = useRevertOccurrenceSnoozeMutation();

  const isSaving = isCreating || isUpdating || isCreatingGroup || isUpdatingGroup;
  const buttonsDisabled = isSaving;

  const latestOccurrence = template?.latestOccurrence ?? null;
  const isEssentialTask = !!template?.groupId || false;

  const swipeRef = useSwipe<HTMLDivElement>({
    onSwipeRight: () => handleClose(),
  });

  const isProjectTemplate = (tmpl?: TaskTemplate | null) => tmpl?.type === 'Project';

  const getDisplayStatus = (): string => {
    const raw = latestOccurrence?.status;
    if (!raw) return 'New';
    return normalizeProjectStatus(String(raw));
  };

  const handleStatusChange = async (nextLabel: string) => {
    const occurrenceId = latestOccurrence?.id;
    if (!occurrenceId) {
      generateToastr(
        'error',
        intl.formatMessage({
          id: 'unableToChangeStatus',
          defaultMessage: 'Unable to change status',
        }),
        intl.formatMessage({
          id: 'noActiveCompletionFound',
          defaultMessage:
            'No active task occurrence found. Run the scheduler before changing status.',
        })
      );
      return;
    }
    setStatusDropdownOpen(false);
    try {
      await setOccurrenceStatus({ occurrenceId, status: displayStatusToApi(nextLabel) }).unwrap();
      onSave?.();
    } catch (e) {
      apiError(e);
    }
  };

  const defaultValues = {
    name: '',
    subTitle: '',
    description: null,
    locationId: '',
    start: moment().toISOString(),
    weeklySchedule: '',
    monthlySameWeekday: false,
    alertIfOverdue: true,
    type: type ?? TaskType.Maintenance,
    interval: type === TaskType.Project ? 1 : 0,
    intervalCarCount: null,
    startingCarCount: null,
    intervalCount: 1,
    hourlySchedule: [],
    prefixId: '',
    linkedTaskTemplates: [],
    priority: 0,
    capacity: null,
    estimatedTime: null,
    employeeId: '',
    applyToAllSitesInGroup: false,
    assetGroupId: null,
    assetItemId: null,
  };

  const updateFormValues = (tmpl: TaskTemplate) => {
    return Object.keys(defaultValues).reduce((acc: any, key: string) => {
      if (key === 'applyToAllSitesInGroup') {
        acc[key] = tmpl.siteId == null && tmpl.groupId != null;
        return acc;
      }
      if (key === 'priority') {
        acc[key] = priorityLabelToIndex((tmpl as any)[key]);
        return acc;
      }
      if (key === 'type') {
        const raw = (tmpl as any)[key];
        acc[key] = typeof raw === 'string' ? (TaskType as Record<string, number>)[raw] ?? raw : raw;
        return acc;
      }
      if (key === 'interval') {
        const raw = (tmpl as any)[key];
        acc[key] = typeof raw === 'string' ? (Interval as Record<string, number>)[raw] ?? raw : raw;
        return acc;
      }
      if (key === 'linkedTaskTemplates') {
        acc[key] = toLinkedTaskTemplateInputs((tmpl as any)[key]);
        return acc;
      }
      const value = (tmpl as any)[key];
      if (value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
  };

  const form = useForm({ defaultValues: template ? updateFormValues(template) : defaultValues });
  const applyToAllSitesInGroup = form.watch('applyToAllSitesInGroup');

  const coerceNumber = (value: any) => {
    if (value != null && value !== '' && !Number.isNaN(value)) return Number(value);
    return null;
  };

  const handlePrimaryClick = () => {
    if (editing) {
      form.handleSubmit(onSubmit, onError)();
    } else {
      setEditing(true);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    if (closingRef.current) clearTimeout(closingRef.current);
    closingRef.current = setTimeout(() => {
      setEditing(false);
    }, drawerTransitionTime);
  };

  const onError = () => {
    generateToastr('error', 'Missing required form fields.', '', { timeOut: 1000 });
  };

  const handleSnooze = async () => {
    const occurrenceId = latestOccurrence?.id;
    if (!occurrenceId) return;
    try {
      if (latestOccurrence?.status !== 'Snoozed') {
        await snoozeOccurrence({ taskId: occurrenceId }).unwrap();
      } else {
        await revertOccurrenceSnooze({ occurrenceId }).unwrap();
      }
      onSave?.();
    } catch (e) {
      apiError(e);
    }
  };

  const onSubmit = async (values: any) => {
    const submitted = _.cloneDeep(values);
    const useGroupRoute = submitted.applyToAllSitesInGroup && groupId != null;
    delete submitted.applyToAllSitesInGroup;

    submitted.type =
      Object.keys(TaskType).find(
        (k) => (TaskType as Record<string, number>)[k] === submitted.type
      ) ?? submitted.type;
    submitted.interval =
      Object.keys(Interval).find(
        (k) => (Interval as Record<string, number>)[k] === submitted.interval
      ) ?? submitted.interval;

    submitted.locationId = coerceNumber(submitted.locationId);
    submitted.startingCarCount = coerceNumber(submitted.startingCarCount);
    submitted.intervalCarCount = coerceNumber(submitted.intervalCarCount) ?? 0;
    submitted.employeeId = coerceNumber(submitted.employeeId);
    submitted.capacity = coerceNumber(submitted.capacity);
    submitted.estimatedTime = submitted.estimatedTime ? String(submitted.estimatedTime) : null;
    submitted.priority = priorityIndexToLabel(submitted.priority);
    submitted.prefixId = submitted.type === 'Project' ? coerceNumber(submitted.prefixId) : null;

    if (submitted.start) {
      if (typeof submitted.start !== 'string') submitted.start = submitted.start.toISOString();
    } else {
      submitted.start = moment().startOf('day').toISOString();
    }
    if (submitted.end) {
      if (typeof submitted.end !== 'string') submitted.end = submitted.end.toISOString();
    }

    submitted.linkedTaskTemplates = (submitted.linkedTaskTemplates || []).map((lt: any) => ({
      id: lt.linkedTask?.value ?? lt.id,
      dependency: lt.dependency?.value ?? lt.dependency,
    }));

    submitted.assetGroupId = submitted.assetGroupId ?? null;
    submitted.assetItemId = submitted.assetItemId ?? null;

    try {
      if (isNew) {
        if (useGroupRoute) {
          await createGroupTask({ groupId: groupId as number, body: submitted }).unwrap();
        } else {
          await createTask(submitted).unwrap();
        }
      } else {
        if (useGroupRoute) {
          await updateGroupTask({
            groupId: groupId as number,
            id: template!.id,
            patch: submitted,
          }).unwrap();
        } else {
          await updateTask({ id: template!.id, ...submitted }).unwrap();
        }
      }
      onSave?.();
      setEditing(false);
    } catch (e) {
      apiError(e);
    }
  };

  const components = {
    Instructions: <TaskInstructionsTab task={template as any} editing={editing} />,
    Details: <TaskDetailsTab task={template as any} type={type} editing={editing} />,
    ...(!isNew && { Attachments: <TaskAttachmentsTab occurrence={latestOccurrence} /> }),
    ...(!isNew && {
      'Linked Tasks': (
        <Controller
          name="linkedTaskTemplates"
          control={form.control}
          render={({ field: { value, onChange } }) => (
            <TaskTemplateLinkedTab
              task={template as any}
              editing={editing}
              value={value || []}
              onChange={onChange}
              disabled={buttonsDisabled}
            />
          )}
        />
      ),
    }),
    ...(!isNew && { Notes: <TaskDetailNotes task={template as any} /> }),
    ...(!isNew && { History: <History task={template as any} /> }),
    ...(!isNew && { Media: <TaskMediaTab task={template as any} /> }),
  };

  useEffect(() => {
    setEditing(isNew || isEditing);
  }, [isNew, isEditing]);

  // A different task opens in the caller's mode: view by default, edit for the
  // work order wizard. isEditing changes on the same task are handled above;
  // keeping it out of these deps stops a mode toggle from wiping the form.
  useEffect(() => {
    if (template) {
      form.reset(updateFormValues(template));
      setEditing(isEditing);
    } else {
      form.reset(defaultValues);
    }
  }, [template?.id]);

  const closeButton = (
    <button className="button task-close" onClick={handleClose} disabled={buttonsDisabled}>
      <span>
        <IoClose />
      </span>
      <span>
        <MdChevronLeft />
        {intl.formatMessage({ id: 'back' })}
      </span>
    </button>
  );

  return (
    <div className="task-details-drawer" ref={swipeRef}>
      <div className="task-details-controls">
        {leadingControls ? (
          <div className="task-actions">
            {leadingControls}
            {closeButton}
          </div>
        ) : (
          closeButton
        )}
        <div className="task-actions">
          <EssentialGuard active={isEssentialTask || applyToAllSitesInGroup}>
            <>
              {template?.id && editing && (
                <button className="button primary inverted" disabled={buttonsDisabled}>
                  {intl.formatMessage({ id: 'delete' })}
                </button>
              )}
              <ButtonWithLoader
                handleClick={handlePrimaryClick}
                isLoading={isSaving}
                disabled={buttonsDisabled}
                style="button primary"
              >
                <>
                  {editing
                    ? intl.formatMessage({ id: 'save' })
                    : intl.formatMessage({ id: 'edit' })}
                </>
              </ButtonWithLoader>
            </>
          </EssentialGuard>
        </div>
      </div>
      {subHeader}
      <div className="task-details-header">
        {editing ? (
          <>
            <div className="form-group padded">
              <label htmlFor="name">{intl.formatMessage({ id: 'taskName' })}</label>
              <Controller
                name="name"
                control={form.control}
                rules={{ required: true }}
                render={({ field }) =>
                  requiredInput({
                    input: field,
                    id: 'name',
                    type: 'text',
                    placeholder: 'Enter name for task',
                  })
                }
              />
            </div>
            <div className="form-group padded">
              <label htmlFor="subTitle">{intl.formatMessage({ id: 'taskSubTitle' })}</label>
              <Controller
                name="subTitle"
                control={form.control}
                render={({ field }) => (
                  <Input
                    input={field}
                    id="subTitle"
                    type="text"
                    placeholder="Enter sub title for task"
                  />
                )}
              />
            </div>
            {groupId != null && (
              <UserGroupPermissions itemRef={viewPermissionRefs.admin}>
                <div className="form-group padded">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Controller
                      name="applyToAllSitesInGroup"
                      control={form.control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          disabled={isEssentialTask || !isNew}
                        />
                      )}
                    />
                    <span>
                      {intl.formatMessage({
                        id: 'applyToAllSitesInGroup',
                        defaultMessage: 'Apply to all sites in this group (essential task)',
                      })}
                    </span>
                  </label>
                </div>
              </UserGroupPermissions>
            )}
          </>
        ) : (
          <>
            {template && (
              <>
                {isEssentialTask && (
                  <span className="badge preventative mb-2">
                    {intl.formatMessage({ id: 'essential' })}
                  </span>
                )}
                <h2>{template.name}</h2>
                {template.subTitle && <p>{template.subTitle}</p>}
              </>
            )}
          </>
        )}
        <div className="task-details-widgets">
          {template && (
            <>
              {latestOccurrence && (
                <button
                  className={`button transparent snooze-button${
                    latestOccurrence.status === 'Snoozed' ? ' snoozed' : ''
                  }`}
                  onClick={handleSnooze}
                >
                  <IoFlag />
                </button>
              )}
              {template.id && isProjectTemplate(template) && (
                <Dropdown
                  isOpen={statusDropdownOpen}
                  toggle={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  className="task-status-dropdown"
                >
                  <DropdownToggle
                    className="button transparent task-status-button"
                    tag="button"
                    type="button"
                    disabled={buttonsDisabled}
                  >
                    {renderStatusPill(getDisplayStatus())}
                    <FaAngleDown />
                  </DropdownToggle>
                  <DropdownMenu>
                    {projectStatusOptions.map((opt) => (
                      <DropdownItem
                        key={opt}
                        onClick={(e) => {
                          e.stopPropagation();
                          setStatusDropdownOpen(false);
                          handleStatusChange(opt);
                        }}
                        active={getDisplayStatus() === opt}
                      >
                        {opt}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              )}
            </>
          )}
        </div>
      </div>
      <div className="task-details-tabs">
        <FormProvider {...form}>
          <ContentTabs
            intl={intl}
            components={components}
            scrollable={true}
            newStyle={true}
            defaultComponent={Number(isNew || editing)}
          />
        </FormProvider>
      </div>
    </div>
  );
};
